import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { toast } from "sonner";

/**
 * Excel-ready CSV: one row per food item, numeric columns without currency
 * symbols so formulas work. Generated entirely in the browser — there is no
 * backend endpoint for this and there should not be one.
 */
export function exportEventCSV({ event, meals, getVendorName, getVendorItemPrice }) {
  if (!event) return;
  const days = event.days || [];
  const rows = [];
  
  // Header
  rows.push(["Day","Date","Day Name","Meal Type","Start Time","End Time","Item Name","Vendor","Category","Unit Price","Quantity","Subtotal","Notes"]);
  
  let grandTotal = 0;
  days.forEach((day) => {
    const dayMeals = meals.filter((m) => m.day_number === day.day_number);
    if (dayMeals.length === 0) {
      rows.push([day.day_number, day.date || "", day.day_name || "", "", "", "", "", "", "", "", "", "", ""]);
    } else {
      dayMeals.forEach((meal) => {
        const items = meal.menu_items || [];
        if (items.length === 0) {
          rows.push([day.day_number, day.date || "", day.day_name || "", meal.meal_type || "", meal.start_time || "", meal.end_time || "", "", "", "", "", "", "", meal.notes || ""]);
        } else {
          items.forEach((item, idx) => {
            let name, vendorName, category, price, qty, subtotal;
            if (typeof item === "object") {
              name = item.name || "";
              vendorName = item.vendor_name || "";
              category = item.category || "";
              price = Number(item.price) || 0;
              qty = Number(item.quantity) || 1;
              subtotal = price * qty;
            } else {
              name = item;
              vendorName = getVendorName(meal.vendor_id) || "";
              category = "";
              price = getVendorItemPrice(meal.vendor_id, item) || 0;
              qty = 1;
              subtotal = price;
            }
            grandTotal += subtotal;
            rows.push([
              day.day_number, day.date || "", day.day_name || "",
              meal.meal_type || "", meal.start_time || "", meal.end_time || "",
              name, vendorName, category,
              price ? price.toFixed(2) : "",
              qty,
              subtotal ? subtotal.toFixed(2) : "",
              idx === 0 ? (meal.notes || "") : ""
            ]);
          });
        }
      });
    }
  });
  
  // Summary
  rows.push([]);
  rows.push(["SUMMARY"]);
  rows.push(["Event", event.title || ""]);
  rows.push(["Location", event.location || ""]);
  rows.push(["Dates", `${event.start_date || ""} to ${event.end_date || ""}`]);
  rows.push(["Status", event.status || ""]);
  rows.push(["Total Days", days.length]);
  rows.push(["Total Meals", meals.length]);
  rows.push(["Grand Total", grandTotal.toFixed(2)]);
  
  // Convert to CSV string
  const csvContent = rows.map(row => 
    row.map(cell => {
      const str = String(cell ?? "");
      return str.includes(",") || str.includes('"') || str.includes("\n") 
        ? `"${str.replace(/"/g, '""')}"` : str;
    }).join(",")
  ).join("\n");
  
  const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${(event.title || "event").replace(/\s+/g, "_")}_meal_plan.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 100);
  toast.success("CSV downloaded!");
}

/**
 * Dashboard-style PDF: cover page, summary cards, cost breakdown by meal type,
 * then one page per event day.
 */
export function exportEventPDF({ event, meals, costBreakdown, getVendorName, getVendorItemPrice, getMealCost }) {
  if (!event) return;
  const doc = new jsPDF();
  const days = event.days || [];
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 14;
  const contentWidth = pageWidth - margin * 2;

  // ── Helper functions ──
  const drawRect = (x, y, w, h, color) => {
    doc.setFillColor(...color);
    doc.roundedRect(x, y, w, h, 2, 2, "F");
  };
  const addPageFooter = () => {
    const pageCount = doc.internal.getNumberOfPages();
    doc.setFontSize(7);
    doc.setTextColor(160, 160, 160);
    doc.text(`IVEM Food Portal | Generated ${new Date().toLocaleDateString()}`, margin, 290);
    doc.text(`Page ${pageCount}`, pageWidth - margin, 290, { align: "right" });
  };

  // ── PAGE 1: Cover / Summary ──
  // Header bar
  drawRect(0, 0, pageWidth, 40, [55, 65, 180]);
  doc.setFontSize(22);
  doc.setTextColor(255, 255, 255);
  doc.text(event.title || "Event Report", margin, 18);
  doc.setFontSize(10);
  doc.text("IVEM Food Portal — Event Meal Plan Report", margin, 28);

  // Event info section
  let y = 50;
  doc.setFontSize(9);
  doc.setTextColor(80, 80, 80);
  const infoItems = [
    ["Location", event.location || "N/A"],
    ["Dates", `${event.start_date || ""} to ${event.end_date || ""}`],
    ["Status", (event.status || "").toUpperCase()],
    ["Days", `${days.length} day(s)`],
    ["Contact", `${event.contact_name || "N/A"}${event.contact_phone ? ` | ${event.contact_phone}` : ""}`],
  ];
  if (event.managed_by_name) infoItems.push(["Manager", event.managed_by_name]);

  // Two-column info layout
  const colW = contentWidth / 2;
  infoItems.forEach((item, i) => {
    const col = i % 2;
    const row = Math.floor(i / 2);
    const xPos = margin + col * colW;
    const yPos = y + row * 8;
    doc.setFont(undefined, "bold");
    doc.setTextColor(60, 60, 60);
    doc.text(`${item[0]}:`, xPos, yPos);
    doc.setFont(undefined, "normal");
    doc.setTextColor(100, 100, 100);
    doc.text(item[1], xPos + 22, yPos);
  });

  y += Math.ceil(infoItems.length / 2) * 8 + 6;

  // ── Summary Cards ──
  const totalMeals = meals.length;
  const totalItems = meals.reduce((s, m) => s + (m.menu_items?.length || 0), 0);
  const grandTotal = costBreakdown.grandTotal;

  drawRect(margin, y, contentWidth, 24, [245, 247, 250]);
  const cardW = contentWidth / 4;
  const summaryCards = [
    { label: "Total Days", value: String(days.length) },
    { label: "Total Meals", value: String(totalMeals) },
    { label: "Total Items", value: String(totalItems) },
    { label: "Grand Total", value: `$${grandTotal.toFixed(2)}` },
  ];
  summaryCards.forEach((card, i) => {
    const cx = margin + i * cardW + cardW / 2;
    doc.setFontSize(14);
    doc.setFont(undefined, "bold");
    doc.setTextColor(55, 65, 180);
    doc.text(card.value, cx, y + 10, { align: "center" });
    doc.setFontSize(7);
    doc.setFont(undefined, "normal");
    doc.setTextColor(120, 120, 120);
    doc.text(card.label, cx, y + 16, { align: "center" });
  });
  y += 32;

  // ── Cost Breakdown Table ──
  doc.setFontSize(11);
  doc.setFont(undefined, "bold");
  doc.setTextColor(40, 40, 40);
  doc.text("Cost Breakdown by Meal Type", margin, y);
  y += 4;

  const breakdownData = Object.entries(costBreakdown.breakdown)
    .filter(([, v]) => v > 0)
    .map(([k, v]) => [k, `$${v.toFixed(2)}`]);

  if (breakdownData.length > 0) {
    breakdownData.push(["GRAND TOTAL", `$${grandTotal.toFixed(2)}`]);
    autoTable(doc, {
      startY: y,
      head: [["Meal Type", "Total Cost"]],
      body: breakdownData,
      theme: "plain",
      headStyles: { fillColor: [55, 65, 180], textColor: [255, 255, 255], fontSize: 8, fontStyle: "bold" },
      bodyStyles: { fontSize: 8, textColor: [60, 60, 60] },
      alternateRowStyles: { fillColor: [248, 249, 252] },
      margin: { left: margin, right: margin },
      tableWidth: contentWidth * 0.5,
      didParseCell: (data) => {
        if (data.row.index === breakdownData.length - 1) {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.fillColor = [235, 237, 255];
          data.cell.styles.textColor = [55, 65, 180];
        }
      },
    });
    y = doc.lastAutoTable.finalY + 10;
  }

  addPageFooter();

  // ── DAILY MEAL PAGES ──
  days.forEach((day) => {
    doc.addPage();
    let dy = 10;

    // Day header
    drawRect(0, 0, pageWidth, 20, [55, 65, 180]);
    doc.setFontSize(13);
    doc.setTextColor(255, 255, 255);
    doc.text(`Day ${day.day_number} — ${day.day_name || ""}`, margin, 13);
    doc.setFontSize(9);
    doc.text(day.date || "", pageWidth - margin, 13, { align: "right" });
    dy = 28;

    const dayMeals = meals.filter((m) => m.day_number === day.day_number);

    if (dayMeals.length === 0) {
      doc.setFontSize(10);
      doc.setTextColor(150, 150, 150);
      doc.text("No meals planned for this day", margin, dy);
    } else {
      dayMeals.forEach((meal) => {
        if (dy > 250) { doc.addPage(); dy = 20; addPageFooter(); }

        const mealCost = getMealCost(meal);
        const timeStr = (meal.start_time || meal.end_time) ? `${meal.start_time || ""} - ${meal.end_time || ""}` : "";

        // Meal type header bar
        drawRect(margin, dy, contentWidth, 8, [235, 237, 255]);
        doc.setFontSize(9);
        doc.setFont(undefined, "bold");
        doc.setTextColor(55, 65, 180);
        doc.text(meal.meal_type || "Meal", margin + 2, dy + 5.5);
        if (timeStr) {
          doc.setFont(undefined, "normal");
          doc.setFontSize(7);
          doc.setTextColor(100, 100, 100);
          doc.text(timeStr, margin + 50, dy + 5.5);
        }
        doc.setFont(undefined, "bold");
        doc.setFontSize(9);
        doc.setTextColor(16, 140, 90);
        doc.text(`$${mealCost.toFixed(2)}`, pageWidth - margin - 2, dy + 5.5, { align: "right" });
        dy += 10;

        // Items table
        const items = (meal.menu_items || []).map((item) => {
          if (typeof item === "string") {
            const price = getVendorItemPrice(meal.vendor_id, item);
            return [item, getVendorName(meal.vendor_id) || "-", "1", price != null ? `$${Number(price).toFixed(2)}` : "-", price != null ? `$${Number(price).toFixed(2)}` : "-"];
          }
          const qty = Number(item.quantity) || 1;
          const price = Number(item.price) || 0;
          return [item.name, item.vendor_name || "-", String(qty), `$${price.toFixed(2)}`, `$${(price * qty).toFixed(2)}`];
        });

        if (items.length > 0) {
          autoTable(doc, {
            startY: dy,
            head: [["Item", "Vendor", "Qty", "Unit Price", "Subtotal"]],
            body: items,
            theme: "plain",
            headStyles: { fillColor: [248, 249, 252], textColor: [80, 80, 80], fontSize: 7, fontStyle: "bold" },
            bodyStyles: { fontSize: 7.5, textColor: [60, 60, 60], cellPadding: 1.5 },
            alternateRowStyles: { fillColor: [253, 253, 255] },
            columnStyles: {
              0: { cellWidth: contentWidth * 0.28 },
              1: { cellWidth: contentWidth * 0.26 },
              2: { cellWidth: contentWidth * 0.08, halign: "center" },
              3: { cellWidth: contentWidth * 0.14, halign: "right" },
              4: { cellWidth: contentWidth * 0.14, halign: "right", fontStyle: "bold", textColor: [16, 140, 90] },
            },
            margin: { left: margin, right: margin },
            tableWidth: contentWidth,
          });
          dy = doc.lastAutoTable.finalY + 2;
        }

        // Notes
        if (meal.notes) {
          doc.setFontSize(7);
          doc.setFont(undefined, "italic");
          doc.setTextColor(130, 130, 130);
          doc.text(`Notes: ${meal.notes}`, margin + 2, dy + 3);
          dy += 6;
        }

        dy += 6;
      });
    }
    addPageFooter();
  });

  const pdfBlob = doc.output("blob");
  const pdfUrl = URL.createObjectURL(pdfBlob);
  window.open(pdfUrl, "_blank");
  toast.success("PDF generated!");
}
